import { type ElementType, type ReactElement, type Ref } from "react";

import { cx, styled } from "simplydo/react/styled";

import { SIZE_FONT } from "../utils/colors";
import type { MenuProps, MenuSize } from "./Menu.types";
import { StyledMenuItem } from "./MenuItem";

export type MenuStyleProps = {
  $vertical?: boolean;
  $fluid?: boolean;
  $size?: MenuSize;
  $stackable?: boolean;
  $widthsPct?: string;
};

export const StyledMenu = styled.div<MenuStyleProps>`
  display: flex;
  margin: 0 0 1rem 0;
  font-family: inherit;
  font-weight: 400;
  border-radius: 0.28571429rem;
  min-height: 2.85714286em;
  background: #ffffff;
  border: 1px solid rgba(34, 36, 38, 0.15);
  box-shadow: 0 1px 2px 0 rgba(34, 36, 38, 0.15);
  flex-direction: ${({ $vertical }) => ($vertical ? "column" : "row")};
  flex-wrap: ${({ $stackable }) => ($stackable ? "wrap" : "nowrap")};
  width: ${({ $fluid, $vertical }) => ($fluid || $vertical ? "100%" : "auto")};
  font-size: ${({ $size }) => ($size && SIZE_FONT[$size]) || SIZE_FONT.medium};

  &.is-compact {
    display: inline-flex;
  }

  &.is-inverted {
    background: #1b1c1d;
  }

  &.is-text,
  &.is-secondary,
  &.is-pointing,
  &.is-tabular {
    background: transparent;
  }

  &.is-secondary:not(.is-pointing) {
    gap: 0.5em;
  }

  &.is-text,
  &.is-secondary,
  &.is-pointing,
  &.is-borderless,
  &.is-tabular {
    border: none;
  }

  &.is-text,
  &.is-secondary,
  &.is-pointing,
  &.is-tabular,
  &.is-borderless {
    box-shadow: none;
  }

  &.is-secondary.is-pointing:not(.is-vertical) {
    border-bottom: 2px solid rgba(34, 36, 38, 0.15);
    border-radius: 0;
  }

  &.is-secondary.is-vertical {
    border: none;
    border-radius: 0;
  }

  &.is-tabular {
    border-bottom: 1px solid rgba(34, 36, 38, 0.15);
    border-radius: 0;
    background: transparent;
  }

  &.is-tabular:not(.is-pointing) {
    margin: 0;
  }

  &.is-widths > ${StyledMenuItem} {
    flex: 1 0 ${({ $widthsPct }) => $widthsPct};
    justify-content: center;
    text-align: center;
  }

  &.is-text > ${StyledMenuItem} {
    padding-left: 0;
    padding-right: 0;
    font-weight: normal;
    color: rgba(0, 0, 0, 0.6);
    box-shadow: none !important;
    border: none !important;
  }

  &.is-secondary.is-pointing:not(.is-vertical) > ${StyledMenuItem}.is-active {
    background: transparent;
    border-bottom: 2px solid #1b1c1d;
    margin-bottom: -2px;
    border-radius: 0;
    font-weight: 700;
    color: rgba(0, 0, 0, 0.95);
  }

  &.is-secondary.is-pointing:not(.is-vertical) > ${StyledMenuItem}:not(.is-active):hover {
    background: transparent;
    border-bottom: 2px solid #acacac;
    margin-bottom: -2px;
    border-radius: 0;
  }

  &.is-secondary.is-vertical > ${StyledMenuItem}.is-active {
    background: transparent;
    border-left: 2px solid #1b1c1d;
    margin-left: -2px;
    font-weight: 700;
  }

  &.is-tabular:not(.is-pointing) > ${StyledMenuItem} {
    border-radius: 0.28571429rem 0.28571429rem 0 0;
    border: 1px solid transparent;
    margin-bottom: -1px;
  }

  &.is-tabular > ${StyledMenuItem}.is-active {
    background: #fff;
    border-color: rgba(34, 36, 38, 0.15);
    border-bottom-color: #fff;
    font-weight: 700;
  }

  ${StyledMenuItem}.is-position-right ~ ${StyledMenuItem}.is-position-right {
    margin-left: 0;
  }
`;

function widthsPct(widths?: number | string): string | undefined {
  if (widths == null) return undefined;
  const n = Number(widths);
  if (!Number.isFinite(n) || n <= 0) return undefined;
  return `${100 / n}%`;
}

const MenuComponent = (
  props: MenuProps & { ref?: Ref<HTMLDivElement> },
): ReactElement => {
  const {
    ref,
    as,
    pointing,
    secondary,
    vertical,
    horizontal,
    fluid,
    size,
    tabular,
    compact,
    inverted,
    color: _color,
    stackable,
    widths,
    borderless,
    text,
    className,
    style,
    children,
  } = props;

  const Component = (as || "div") as ElementType;
  const pct = widthsPct(widths);

  return (
    <StyledMenu
      as={Component}
      ref={ref}
      className={cx(
        className,
        pointing && "is-pointing",
        secondary && "is-secondary",
        vertical && "is-vertical",
        horizontal && "horizontal",
        fluid && "is-fluid",
        tabular && "is-tabular",
        compact && "is-compact",
        inverted && "is-inverted",
        borderless && "is-borderless",
        text && "is-text",
        pct && "is-widths",
        size,
      )}
      style={style}
      $vertical={vertical}
      $fluid={fluid}
      $size={size}
      $stackable={stackable}
      $widthsPct={pct}
    >
      {children}
    </StyledMenu>
  );
};

export const Menu = styled(MenuComponent)``;
